import { Observable, ApolloLink } from "@apollo/client";
import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { onError } from "@apollo/client/link/error";
import type { GraphQLFormattedError } from "graphql";
import { toast } from "sonner";
import * as Sentry from "@sentry/react";

/** `extensions.code` Jupiter puts on a field its offboarding gate blocked. */
const TENANT_OFFBOARDED_CODE = "TENANT_OFFBOARDED";

// Observable Observer interface for Apollo Client's Observable callback
interface ObservableObserver {
  next: (value: unknown) => void;
  error: (error: unknown) => void;
  complete: () => void;
}

export interface TokenRefreshResult {
  access_token: string;
}

export interface AuthErrorLinkOptions {
  /**
   * Function to refresh the access token. Should return an object with access_token on success,
   * or null/throw on failure.
   */
  onTokenRefresh: () => Promise<TokenRefreshResult | null>;

  /**
   * Called when token refresh definitively fails (session expired, invalid grant, etc.).
   * Should handle cleanup (remove user, clear state) and typically reload the page.
   * NOT called for temporary network errors - only for definitive auth failures.
   */
  onTokenRefreshFailed: (error: unknown) => void;

  /**
   * Called when token refresh succeeds. Can be used to update global token state.
   * Optional - if not provided, only the operation context is updated.
   */
  onTokenRefreshSuccess?: (token: string) => void;

  /**
   * Called when a GraphQL schema mismatch error is detected.
   * This occurs when the frontend queries fields that no longer exist in the backend.
   */
  onSchemaMismatch?: (errorMessage: string) => void;

  /**
   * Called when the backend rejects a request with the TENANT_NOT_LICENSED
   * code (HTTP 403) -- an authenticated user (e.g. via the Microsoft broker)
   * whose email domain has no tenant_domain mapping. Not an auth failure:
   * the token is valid, the account is simply not provisioned for any
   * tenant. Should render a distinct "not yet set up" screen instead of
   * retrying or logging out.
   */
  onTenantNotLicensed?: () => void;

  /**
   * Prefix for console log messages. Defaults to "Apollo".
   */
  logPrefix?: string;

  /**
   * Maximum number of retry attempts for token refresh. Defaults to 3.
   */
  maxRetries?: number;

  /**
   * Base delay in milliseconds between retries. Actual delay increases with each attempt.
   * Defaults to 1000ms.
   */
  retryDelayMs?: number;
}

/**
 * Check if an error message indicates a definitive auth failure that requires re-login.
 * These errors indicate the session is truly invalid and cannot be recovered by retrying.
 */
function isDefinitiveAuthFailure(errorMessage: string): boolean {
  return (
    errorMessage.includes("login_required") ||
    errorMessage.includes("invalid_grant") ||
    errorMessage.includes("consent_required") ||
    errorMessage.includes("interaction_required") ||
    errorMessage.includes("session_expired")
  );
}

/**
 * The login session is over: the server rejected the access token (401) and the refresh
 * failed for a reason that is not a connectivity problem. Callers show "session expired"
 * and a login button instead of a connection error. Matched by `name` as well as by class,
 * because the class can be duplicated across bundles.
 */
export class SessionExpiredError extends Error {
  constructor(message = "Session expired") {
    super(message);
    this.name = "SessionExpiredError";
  }
}

export function isSessionExpiredError(error: unknown): boolean {
  return (
    error instanceof SessionExpiredError ||
    (error instanceof Error && error.name === "SessionExpiredError")
  );
}

/**
 * A refresh failure that says nothing about the session: the request never reached the
 * login server (offline, DNS, blocked). Retrying later can succeed, so these never log out.
 * Anything else after the retries (an iframe that times out, a missing refresh token, an
 * unexpected response) means the session cannot be renewed, and waiting does not help.
 */
function isTemporaryNetworkFailure(errorMessage: string): boolean {
  return /failed to fetch|networkerror|network error|network request failed|load failed|err_internet_disconnected/i.test(
    errorMessage,
  );
}

/**
 * Checks if a GraphQL error indicates an authentication failure.
 */
function isAuthError(error: GraphQLFormattedError): boolean {
  return (
    error.extensions?.code === "UNAUTHENTICATED" ||
    error.extensions?.code === "UNAUTHORIZED" ||
    error.message?.toLowerCase().includes("unauthorized") ||
    error.message?.toLowerCase().includes("unauthenticated") ||
    error.message?.toLowerCase().includes("not authenticated")
  );
}

/**
 * Checks if a GraphQL error indicates a schema mismatch.
 */
function isSchemaMismatchError(error: GraphQLFormattedError): boolean {
  const message = error.message || "";
  return (
    message.includes("Cannot query field") ||
    message.includes("Unknown type") ||
    (message.includes("Field") && message.includes("doesn't exist"))
  );
}

/**
 * Creates an Apollo error link that handles:
 * - Network-level 401 errors with automatic token refresh and retries
 * - GraphQL-level authentication errors with automatic token refresh and retries
 * - Schema mismatch errors (when frontend queries non-existent fields)
 * - General error logging
 *
 * Key features:
 * - Retries token refresh multiple times before giving up
 * - Distinguishes between definitive auth failures (login_required, invalid_grant)
 *   and temporary network errors
 * - Only logs out user on definitive auth failures, not on network errors
 * - Prevents cascading auth error handling
 *
 * This error link is designed to be shared between Venus and Neptune to avoid
 * code duplication for auth error handling logic.
 */
export function createAuthErrorLink(options: AuthErrorLinkOptions) {
  const {
    onTokenRefresh,
    onTokenRefreshFailed,
    onTokenRefreshSuccess,
    onSchemaMismatch,
    onTenantNotLicensed,
    logPrefix = "Apollo",
    maxRetries = 3,
    retryDelayMs = 1000,
  } = options;

  // Track if we're currently handling an auth error to prevent cascading logouts
  let isHandlingAuthError = false;

  /**
   * Attempt token refresh with retries - only triggers logout on definitive auth failure
   */
  const attemptTokenRefreshWithRetry = async (): Promise<{
    success: boolean;
    accessToken?: string;
    shouldLogout: boolean; // true = definitive auth failure, false = network/temporary error
  }> => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const result = await onTokenRefresh();
        if (result?.access_token) {
          return { success: true, accessToken: result.access_token, shouldLogout: false };
        }
        throw new Error("No access token after refresh");
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);

        // These errors indicate the session is truly invalid and requires re-authentication
        if (isDefinitiveAuthFailure(errorMessage)) {
          console.warn(`${logPrefix} - Definitive auth failure: ${errorMessage}`);
          return { success: false, shouldLogout: true };
        }

        // Network/timeout errors - retry
        if (attempt < maxRetries) {
          const delay = retryDelayMs * attempt;
          console.log(
            `${logPrefix} - Token refresh attempt ${attempt} failed (${errorMessage}), retrying in ${delay}ms...`,
          );
          await new Promise((resolve) => setTimeout(resolve, delay));
        } else {
          // The server has already said the token is expired. A refresh that still fails
          // for a reason other than connectivity means the session is gone: log out so the
          // user lands on the login page, not on a connection error they cannot clear.
          const temporary = isTemporaryNetworkFailure(errorMessage);
          console.warn(
            `${logPrefix} - Token refresh failed after ${maxRetries} attempts (${errorMessage}); ${
              temporary ? "network problem, not logging out" : "session cannot be renewed, logging out"
            }`,
          );
          return { success: false, shouldLogout: !temporary };
        }
      }
    }
    return { success: false, shouldLogout: false };
  };

  /**
   * Handle auth error by attempting token refresh and retrying the operation
   */
  const handleAuthError = (
    operation: ApolloLink.Operation,
    forward: ApolloLink.ForwardFunction,
    observer: ObservableObserver,
  ) => {
    attemptTokenRefreshWithRetry()
      .then((result) => {
        isHandlingAuthError = false;
        if (result.success && result.accessToken) {
          console.log(`${logPrefix} errorLink - Token refreshed successfully, retrying request`);

          // Notify callback if provided
          onTokenRefreshSuccess?.(result.accessToken);

          // Update the operation context with new token
          operation.setContext(
            ({ headers = {} }: { headers?: Record<string, string> }) => ({
              headers: {
                ...headers,
                authorization: `Bearer ${result.accessToken}`,
              },
            }),
          );

          // Retry the request with the new token
          const subscriber = {
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
          };
          forward(operation).subscribe(subscriber);
        } else if (result.shouldLogout) {
          // Only logout on definitive auth failure (session truly expired)
          console.error(`${logPrefix} errorLink - Token refresh definitively failed, logging out`);
          onTokenRefreshFailed(new SessionExpiredError("Authentication failed"));
          observer.error(new SessionExpiredError("Authentication failed"));
        } else {
          // Network/temporary error - don't logout, let user retry
          console.warn(
            `${logPrefix} errorLink - Token refresh failed due to network error, not logging out`,
          );
          observer.error(new Error("Network error during authentication refresh"));
        }
      })
      .catch((error) => {
        isHandlingAuthError = false;
        console.error(`${logPrefix} errorLink - Unexpected error during token refresh:`, error);
        observer.error(error);
      });
  };

  return onError(
    ({ error, operation, forward }) => {
      // Apollo Client 4 hands the handler one `error`, not the v3 pair
      // `graphQLErrors` / `networkError`. Destructuring the old names gave `undefined`
      // for both, so no 401 ever reached the token refresh below. A GraphQL error carries
      // its list; anything else is a transport failure (ServerError has `statusCode`).
      const graphQLErrors: ReadonlyArray<GraphQLFormattedError> | undefined =
        CombinedGraphQLErrors.is(error) ? error.errors : undefined;
      const networkError = CombinedGraphQLErrors.is(error)
        ? undefined
        : (error as Error & { statusCode?: number });

      // Log all network errors for debugging
      if (networkError) {
        console.log(`${logPrefix} errorLink - Network error detected:`, {
          message: networkError.message,
          name: networkError.name,
          statusCode:
            "statusCode" in networkError ? networkError.statusCode : undefined,
          error: networkError,
        });

        // Detect CORS errors — they appear as "Failed to fetch" with no status code
        if (
          networkError.message === "Failed to fetch" &&
          !("statusCode" in networkError && networkError.statusCode)
        ) {
          console.error(
            `${logPrefix} errorLink - Possible CORS error: the API server is not allowing requests from this origin`,
          );
          Sentry.captureException(networkError, {
            extra: { client: logPrefix, operation: operation.operationName, type: "CORS" },
          });
          toast.error("Verbindung zum Server blockiert", {
            description:
              "Die Anfrage wurde vom Server abgelehnt (CORS). Bitte prüfen Sie die Server-Konfiguration oder verwenden Sie die korrekte Umgebung.",
            duration: 10000,
          });
          return;
        }

        // Report unexpected network errors to Sentry. 401/403 are handled below;
        // 410 is Jupiter's offboarding gate, expected for every request an
        // offboarded tenant's open client still sends.
        const statusCode = "statusCode" in networkError ? networkError.statusCode : undefined;
        if (statusCode !== 401 && statusCode !== 403 && statusCode !== 410) {
          Sentry.captureException(networkError, {
            extra: { client: logPrefix, operation: operation.operationName, statusCode },
          });
        }
      }

      // Check for network-level 403 with the TENANT_NOT_LICENSED code. This is
      // NOT an auth failure (the token is valid) -- it means the Microsoft-
      // broker-authenticated user's email domain has no tenant_domain row.
      // Must be checked before the generic 401/403 auth-retry handling below
      // so we don't attempt a token refresh for something a refresh can't fix.
      if (
        networkError &&
        "statusCode" in networkError &&
        networkError.statusCode === 403
      ) {
        const result = (networkError as { result?: unknown }).result as
          | { detail?: { code?: string } }
          | undefined;
        if (result?.detail?.code === "TENANT_NOT_LICENSED") {
          console.warn(`${logPrefix} errorLink - Tenant not licensed for this account`);
          onTenantNotLicensed?.();
          return;
        }
      }

      // Check for network-level 401 (HTTP status code)
      if (
        networkError &&
        "statusCode" in networkError &&
        networkError.statusCode === 401
      ) {
        // Prevent cascading auth handling
        if (isHandlingAuthError) {
          console.log(`${logPrefix} errorLink - Already handling auth error, skipping`);
          return;
        }
        isHandlingAuthError = true;

        // A routine expiry, not a failure: warn would make it a Sentry issue.
        console.info(
          `${logPrefix} errorLink - 401 Unauthorized (network), attempting token refresh`,
        );

        // Try to refresh the token silently using Observable (Apollo Client v4)
        return new Observable((observer) => {
          handleAuthError(operation, forward, observer as ObservableObserver);
        });
      }

      // Check for GraphQL errors
      if (graphQLErrors) {
        // Check for schema mismatch errors first (before auth errors)
        if (onSchemaMismatch) {
          const schemaError = graphQLErrors.find(isSchemaMismatchError);
          if (schemaError) {
            console.error(
              `${logPrefix} errorLink - GraphQL schema mismatch detected`,
            );
            onSchemaMismatch(schemaError.message || "Unknown schema error");
            return;
          }
        }

        // Check for authentication errors
        const hasAuthError = graphQLErrors.some(isAuthError);

        if (hasAuthError) {
          // Prevent cascading auth handling
          if (isHandlingAuthError) {
            console.log(`${logPrefix} errorLink - Already handling auth error, skipping`);
            return;
          }
          isHandlingAuthError = true;

          // A routine expiry, not a failure: warn would make it a Sentry issue.
          console.info(
            `${logPrefix} errorLink - Authentication error in GraphQL response, attempting token refresh`,
          );

          // Try to refresh the token silently using Observable (Apollo Client v4)
          return new Observable((observer) => {
            handleAuthError(operation, forward, observer as ObservableObserver);
          });
        }

        // Log and report other GraphQL errors. A field blocked by the offboarding
        // gate is expected; a paused tenant gets it with HTTP 200, so the 410
        // check above does not catch it.
        const unexpectedErrors = graphQLErrors.filter(
          (gqlError) => gqlError.extensions?.code !== TENANT_OFFBOARDED_CODE,
        );
        unexpectedErrors.forEach((gqlError: GraphQLFormattedError) => {
          const { message, locations, path, extensions } = gqlError;
          // Not error: Neptune's console capture would report it a second time
          // next to the captureException below.
          console.info(
            `[${logPrefix} GraphQL error]: Message: ${message}, Code: ${extensions?.code || "N/A"}, Location: ${JSON.stringify(locations)}, Path: ${path}`,
          );
          Sentry.captureException(new Error(`[${logPrefix}] GraphQL error: ${message}`), {
            extra: {
              client: logPrefix,
              operation: operation.operationName,
              code: extensions?.code,
              locations,
              path,
            },
          });
        });
      }
    },
  );
}
